import React, { useState, useRef } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { motion } from 'framer-motion';
import { FiFileText, FiX, FiCheck, FiMail, FiPhone, FiCalendar, FiUser, FiCreditCard } from 'react-icons/fi';
import { toast } from 'react-toastify';
import './AgreementModal.css';

const AgreementModal = ({ isOpen, onClose, onSubmit, isLoading, creatorName, creatorEmail }) => {
  const sigCanvas = useRef(null);
  const [formData, setFormData] = useState({
    signatureDate: new Date().toISOString().split('T')[0],
    fullName: creatorName || '',
    idNumber: '',
    invoiceEmail: creatorEmail || '',
    contactPhone: '',
    sendCopy: false
  });
  const [showAgreement, setShowAgreement] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);

  const clearSignature = () => {
    sigCanvas.current?.clear();
    setHasSignature(false);
  };

  const handleSignatureEnd = () => {
    if (sigCanvas.current && !sigCanvas.current.isEmpty()) {
      setHasSignature(true);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const validateForm = () => {
    if (!formData.signatureDate) {
      toast.error('נא לבחור תאריך חתימה');
      return false;
    }
    if (!formData.fullName || formData.fullName.length < 2) {
      toast.error('נא להזין שם מלא');
      return false;
    }
    if (!formData.idNumber || !/^\d{9}$/.test(formData.idNumber)) {
      toast.error('נא להזין מספר תעודת זהות תקין (9 ספרות)');
      return false;
    }
    if (!formData.invoiceEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.invoiceEmail)) {
      toast.error('נא להזין כתובת מייל תקינה');
      return false;
    }
    if (!formData.contactPhone || formData.contactPhone.length < 9) {
      toast.error('נא להזין מספר טלפון תקין');
      return false;
    }
    if (!hasSignature) {
      toast.error('נא לחתום על ההסכם');
      return false;
    }
    return true;
  };

  const handleSubmit = () => {
    if (!validateForm()) return;

    const signatureData = sigCanvas.current?.toDataURL('image/png');

    onSubmit({
      ...formData,
      signature: signatureData,
      signedAt: new Date().toISOString()
    });
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <motion.div
        className="agreement-modal"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        onClick={(e) => e.stopPropagation()}
      >
        <button className="modal-close-btn" onClick={onClose}>
          <FiX />
        </button>

        <div className="agreement-modal-content">
          {/* Agreement View Button */}
          <div className="agreement-view-section">
            <button
              className="view-agreement-btn"
              onClick={() => setShowAgreement(true)}
            >
              <FiFileText />
              צפייה בהסכם
            </button>
          </div>

          {/* Form Fields */}
          <div className="agreement-form">
            <h3>פרטי המשפיען/נית</h3>

            <div className="form-group">
              <label>
                תאריך מועד החתימה <span className="required">*</span>
              </label>
              <div className="input-with-icon">
                <FiCalendar className="input-icon" />
                <input
                  type="date"
                  name="signatureDate"
                  value={formData.signatureDate}
                  onChange={handleInputChange}
                  className="form-input"
                />
              </div>
            </div>

            <div className="form-group">
              <label>
                שם מלא <span className="required">*</span>
              </label>
              <div className="input-with-icon">
                <FiUser className="input-icon" />
                <input
                  type="text"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleInputChange}
                  placeholder="שם פרטי ומשפחה"
                  className="form-input"
                />
              </div>
            </div>

            <div className="form-group">
              <label>
                תעודת זהות <span className="required">*</span>
              </label>
              <div className="input-with-icon">
                <FiCreditCard className="input-icon" />
                <input
                  type="text"
                  name="idNumber"
                  value={formData.idNumber}
                  onChange={handleInputChange}
                  placeholder="תעודת זהות"
                  className="form-input"
                  dir="ltr"
                  maxLength={9}
                />
              </div>
            </div>

            <div className="form-group">
              <label>
                דוא"ל <span className="required">*</span>
              </label>
              <div className="input-with-icon">
                <FiMail className="input-icon" />
                <input
                  type="email"
                  name="invoiceEmail"
                  value={formData.invoiceEmail}
                  onChange={handleInputChange}
                  placeholder="example@email.com"
                  className="form-input"
                />
              </div>
            </div>

            <div className="form-group">
              <label>
                טלפון <span className="required">*</span>
              </label>
              <div className="input-with-icon">
                <FiPhone className="input-icon" />
                <input
                  type="tel"
                  name="contactPhone"
                  value={formData.contactPhone}
                  onChange={handleInputChange}
                  placeholder="050-0000000"
                  className="form-input"
                  dir="ltr"
                />
              </div>
            </div>

            {/* Signature Section */}
            <div className="form-group signature-section">
              <label>
                חתימה <span className="required">*</span>
              </label>
              <div className="signature-container">
                <SignatureCanvas
                  ref={sigCanvas}
                  canvasProps={{
                    className: 'signature-canvas',
                    width: 350,
                    height: 150
                  }}
                  onEnd={handleSignatureEnd}
                />
                <button
                  type="button"
                  className="clear-signature-btn"
                  onClick={clearSignature}
                >
                  <FiX />
                </button>
              </div>
              <p className="signature-hint">חתום באצבע או בעכבר</p>
            </div>

            {/* Send Copy Checkbox */}
            <div className="form-group checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="sendCopy"
                  checked={formData.sendCopy}
                  onChange={handleInputChange}
                />
                <span>לשלוח העתק למייל שלך?</span>
              </label>
            </div>

            {/* Agreement Link - accessible near submit */}
            <a
              href="/CustomerAgreement.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="agreement-link-bottom"
            >
              <FiFileText />
              צפייה בהסכם שיתוף פעולה
            </a>

            {/* Submit Button */}
            <button
              className="submit-agreement-btn"
              onClick={handleSubmit}
              disabled={isLoading}
            >
              {isLoading ? (
                'שולח...'
              ) : (
                <>
                  <FiCheck />
                  שלחו את הטופס החתום
                </>
              )}
            </button>
          </div>
        </div>

        {/* Agreement PDF Popup */}
        {showAgreement && (
          <div className="agreement-popup-overlay" onClick={() => setShowAgreement(false)}>
            <motion.div
              className="agreement-popup pdf-popup"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="agreement-popup-header">
                <h3>הסכם שיתוף פעולה - AIweOn</h3>
                <button onClick={() => setShowAgreement(false)}>
                  <FiX />
                </button>
              </div>
              <div className="agreement-popup-content pdf-content">
                <iframe
                  src="/CustomerAgreement.pdf"
                  title="הסכם שיתוף פעולה"
                  className="pdf-viewer"
                />
              </div>
              <div className="agreement-popup-footer">
                <a
                  href="/CustomerAgreement.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-outline"
                >
                  פתח בחלון חדש
                </a>
                <button className="btn btn-primary" onClick={() => setShowAgreement(false)}>
                  סגור
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default AgreementModal;
